/**
 * Connection Status Component
 * Displays bank connection status and sync info
 */
import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { formatRelativeTime } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { Connection } from '@/types';

interface ConnectionStatusProps {
  connections: Connection[];
  isLoading?: boolean;
}

function StatusBadge({ status }: { status: Connection['status'] }) {
  const styles: Record<Connection['status'], string> = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    pending_reauth: 'bg-orange-100 text-orange-800',
    disconnected: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<Connection['status'], string> = {
    active: 'Connected',
    pending: 'Pending',
    error: 'Error',
    pending_reauth: 'Needs Reauth',
    disconnected: 'Disconnected',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        styles[status]
      )}
    >
      {labels[status]}
    </span>
  );
}

function ConnectionSkeleton() {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
        <div className="mt-1 h-3 w-20 animate-pulse rounded bg-gray-100" />
      </div>
      <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-8 text-center">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">No banks connected</h3>
      <p className="mt-1 text-sm text-gray-500">
        Link your bank accounts to get started tracking your finances.
      </p>
      <div className="mt-4">
        <Link to="/connections">
          <Button variant="primary" size="sm">
            Connect Bank
          </Button>
        </Link>
      </div>
    </div>
  );
}

function BankIcon() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
      <svg
        className="h-6 w-6 text-gray-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    </div>
  );
}

export default function ConnectionStatus({ connections, isLoading }: ConnectionStatusProps) {
  const hasErrors = connections.some(
    (c) => c.status === 'error' || c.status === 'pending_reauth' || c.status === 'disconnected'
  );

  return (
    <Card>
      <CardHeader
        title="Bank Connections"
        description={
          hasErrors
            ? 'Some connections need attention'
            : connections.length > 0
            ? 'All connections healthy'
            : 'Connect your banks'
        }
        action={
          connections.length > 0 ? (
            <Link
              to="/connections"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Manage
            </Link>
          ) : null
        }
      />

      {isLoading ? (
        <div className="divide-y divide-gray-100">
          {[1, 2].map((i) => (
            <ConnectionSkeleton key={i} />
          ))}
        </div>
      ) : connections.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="divide-y divide-gray-100">
          {connections.map((connection) => {
            // Safely handle potentially undefined properties
            const institutionName = connection.institutionName || 'Unknown Institution';
            const accountsCount = Number(connection.accountsCount) || 0;
            const status = connection.status || 'pending';

            return (
              <div key={connection.id} className="flex items-center gap-3 py-3">
                <BankIcon />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {institutionName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {accountsCount} account{accountsCount !== 1 ? 's' : ''}
                    {connection.lastSyncAt && (
                      <>
                        {' '}
                        <span className="mx-1 text-gray-300">|</span>
                        Last synced {formatRelativeTime(connection.lastSyncAt)}
                      </>
                    )}
                  </p>
                </div>
                <StatusBadge status={status} />
              </div>
            );
          })}

          {/* Add another bank link */}
          <div className="pt-3">
            <Link
              to="/connections"
              className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add another bank
            </Link>
          </div>
        </div>
      )}
    </Card>
  );
}
